import { toBase, UNIT_DIMENSION } from "./quantity.js";
/**
 * Money, in integer paise.
 *
 * Never a float — see AGENTS.md rule 7. `₹68.50` is 6850, and every arithmetic
 * step here stays in paise until the final render, so a rupee can never be lost
 * to binary rounding.
 *
 * A price is the one fact on an item that makes waste FELT. "Threw away 200 g of
 * paneer" is a shrug; "threw away ₹90" is not. That is the whole reason this
 * module exists, and it is why the unit price matters as much as the price: the
 * comparison people actually make in a shop is per kilo, not per pack.
 */
/** Beyond this a price is a typo, not a grocery. ₹99,999. */
export const MAX_PRICE_MINOR = 9_999_900;
/**
 * "₹68", "₹68.50".
 *
 * Paise are dropped when they are zero, because a kitchen app writing "₹68.00"
 * reads like an invoice. They are shown to exactly two places otherwise, since
 * that is what a price IS — there is no such thing as a third decimal of a
 * rupee, so rendering one would be inventing precision.
 */
export function formatMoney(paise) {
    if (!Number.isFinite(paise))
        return "";
    const whole = Math.trunc(Math.abs(paise) / 100);
    const rem = Math.abs(paise) % 100;
    const sign = paise < 0 ? "-" : "";
    const rupees = whole.toLocaleString("en-IN");
    return rem === 0
        ? `${sign}₹${rupees}`
        : `${sign}₹${rupees}.${String(rem).padStart(2, "0")}`;
}
/**
 * Reads what someone typed into a price box, in rupees, and returns paise.
 *
 * Tolerant of the ways a price actually gets typed — "₹68", "68.5", " 1,250 " —
 * and null for everything else. Null rather than 0, because "0" and "I have not
 * said" are different facts and only one of them should ever be stored.
 */
export function parseRupees(raw) {
    const cleaned = raw.replace(/[₹,\s]/g, "");
    if (cleaned === "")
        return null;
    if (!/^\d*\.?\d*$/.test(cleaned))
        return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    // Rounded at the paise, not floored: 68.999 typed into a box is ₹69, and
    // truncating would quietly under-report every total built on top of it.
    const paise = Math.round(n * 100);
    return Math.min(paise, MAX_PRICE_MINOR);
}
/**
 * What it works out to per standard unit — "₹340 per kg", "₹68 per litre".
 *
 * Stated per kilo and per litre rather than per 100 g, because that is the unit
 * an Indian shelf label uses and the point of this line is to be comparable to
 * the shelf. Countable things get "each": a per-kilo price for a dozen eggs is
 * arithmetic nobody asked for.
 *
 * Null when there is no quantity to divide by — a price with no size is still a
 * useful fact, it just cannot be normalised, and a fabricated denominator would
 * make every comparison wrong rather than missing.
 */
export function unitPrice(paise, q) {
    if (!q || !Number.isFinite(paise) || paise <= 0)
        return null;
    const base = toBase(q);
    if (!Number.isFinite(base) || base <= 0)
        return null;
    const dimension = UNIT_DIMENSION[q.unit];
    if (dimension === "count") {
        // One of something is not a rate — "₹68 per piece" for a single piece is
        // just the price again, said twice.
        if (base <= 1)
            return null;
        return `${formatMoney(Math.round(paise / base))} each`;
    }
    // base is ml or g; the shelf states litres and kilos.
    const per = Math.round((paise / base) * 1000);
    return `${formatMoney(per)} per ${dimension === "volume" ? "litre" : "kg"}`;
}
//# sourceMappingURL=money.js.map